/**
 * Where to Buy 数据加载器 - 重构版本
 * 适配 where_to_buy.json 数据结构
 */

/**
 * @typedef {Object} RetailerInfo
 * @property {string} type - 零售商名称
 * @property {string} channel_url - 零售商URL
 * @property {string} logo_url - 零售商logo URL
 */

/**
 * @typedef {Object} CountryData
 * @property {string} name - 国家名称
 * @property {string} country_code - 国家代码
 * @property {string} continent - 所属大洲
 * @property {RetailerInfo[]} online_stores - 在线商店列表
 * @property {RetailerInfo[]} resellers - 零售商列表
 * @property {RetailerInfo[]} distributors - 分销商列表
 */

/**
 * @typedef {Object} RegionData
 * @property {string} name - 地区名称
 * @property {string} continent - 所属大洲
 * @property {Object[]} countries - 国家数据数组，每个元素包含一个国家对象
 */

/**
 * @typedef {Record<string, RegionData>} WhereToBuyData
 */

// 全局变量存储数据
/** @type {WhereToBuyData | null} */
let whereToBuyData = null;

/**
 * 加载 Where to Buy 数据
 * @returns {Promise<WhereToBuyData>} 返回加载的数据
 */
async function loadWhereToBuyData() {
  try {
    // 如果数据已经加载过，直接返回
    if (whereToBuyData) {
      return whereToBuyData;
    }

    console.log('Loading Where to Buy data...');

    // 从 JSON 文件加载数据，使用从 Liquid 模板传递的路径
    const dataOrigin =
      /** @type {any} */ (window).whereToBuyDataOrigin ||
      '/cdn/shop/t/19/assets/where_to_buy.json';
    console.log('Data origin URL:', dataOrigin);
    const response = await fetch(dataOrigin);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    whereToBuyData = /** @type {WhereToBuyData} */ (data);

    console.log('Where to Buy 数据加载成功:', whereToBuyData);
    console.log('Available regions:', Object.keys(whereToBuyData));

    return whereToBuyData;
  } catch (error) {
    console.error('加载 Where to Buy 数据时出错:', error);

    // 使用默认数据作为后备
    whereToBuyData = /** @type {WhereToBuyData} */ ({});

    return whereToBuyData;
  }
}

/**
 * 获取 Where to Buy 数据（同步方式）
 * @returns {WhereToBuyData|null} 返回数据或 null（如果未加载）
 */
function getWhereToBuyData() {
  return whereToBuyData;
}

/**
 * 获取所有地区列表
 * @returns {Array<{id: string, name: string}>} 地区列表
 */
function getRegions() {
  const data = getWhereToBuyData();
  if (!data) return [];

  return Object.keys(data).map((regionId) => ({
    id: regionId,
    name: data[regionId]?.name || regionId,
  }));
}

/**
 * 获取指定地区的所有国家
 * @param {string} regionId - 地区ID
 * @returns {Array<{id: string, name: string}>} 国家列表（按A-Z排序）
 */
function getCountriesByRegion(regionId) {
  const data = getWhereToBuyData();
  if (!data || !data[regionId] || !data[regionId].countries) return [];

  /** @type {Array<{id: string, name: string}>} */
  const countries = [];
  const region = data[regionId];
  if (region && region.countries) {
    region.countries.forEach(
      /** @param {any} countryObj */ (countryObj) => {
        Object.keys(countryObj).forEach((countryId) => {
          const country = /** @type {CountryData} */ (
            /** @type {unknown} */ (countryObj[countryId])
          );
          if (country) {
            countries.push({
              id: countryId,
              name: country.name,
            });
          }
        });
      },
    );
  }

  // 按国家名称A-Z排序
  countries.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );

  return countries;
}

/**
 * 获取所有国家（跨所有地区）
 * @returns {Array<{id: string, name: string, regionId: string}>} 国家列表（按A-Z排序）
 */
function getAllCountries() {
  const data = getWhereToBuyData();
  if (!data) return [];

  /** @type {Array<{id: string, name: string, regionId: string}>} */
  const countries = [];
  Object.keys(data).forEach((regionId) => {
    const regionCountries = getCountriesByRegion(regionId);
    regionCountries.forEach((country) => {
      countries.push({
        ...country,
        regionId: regionId,
      });
    });
  });

  // 按国家名称A-Z排序
  countries.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );

  return countries;
}

/**
 * 获取指定国家的零售商数据
 * @param {string} regionId - 地区ID
 * @param {string} countryId - 国家ID
 * @param {string} retailerType - 零售商类型 ('online_stores', 'resellers', 'distributors')
 * @returns {RetailerInfo[]} 零售商列表
 */
function getRetailersByCountry(regionId, countryId, retailerType) {
  const data = getWhereToBuyData();
  if (!data || !data[regionId] || !data[regionId].countries) return [];

  const region = data[regionId];
  if (region && region.countries) {
    for (const countryObj of region.countries) {
      if (/** @type {any} */ (countryObj)[countryId]) {
        const country = /** @type {CountryData} */ (
          /** @type {unknown} */ (/** @type {any} */ (countryObj)[countryId])
        );
        switch (retailerType) {
          case 'online_stores':
            return country.online_stores || [];
          case 'resellers':
            return country.resellers || [];
          case 'distributors':
            return country.distributors || [];
          default:
            return [];
        }
      }
    }
  }

  return [];
}

/**
 * 初始化选择器和事件处理
 * @param {string} sectionId - section ID
 */
async function initWhereToBuySection(sectionId) {
  console.log('initWhereToBuySection called with sectionId:', sectionId);

  // 确保数据已加载
  const data = await loadWhereToBuyData();
  console.log('Data loaded:', data);

  const regionSelect = /** @type {HTMLElement | null} */ (
    document.querySelector(`#region-select-${sectionId}`)
  );
  const countrySelect = /** @type {HTMLElement | null} */ (
    document.querySelector(`#country-select-${sectionId}`)
  );
  const tabsContainer = /** @type {HTMLElement | null} */ (
    document.querySelector(`.where-to-buy-tabs[data-section-id="${sectionId}"]`)
  );

  console.log('Elements found:', {
    regionSelect: !!regionSelect,
    countrySelect: !!countrySelect,
    tabsContainer: !!tabsContainer,
  });

  if (!regionSelect || !countrySelect || !tabsContainer) {
    console.error('Required elements not found:', {
      regionSelect: regionSelect,
      countrySelect: countrySelect,
      tabsContainer: tabsContainer,
    });
    return;
  }

  // 初始化 sticky tabs header
  initStickyTabsHeader(sectionId);

  // 初始化地区选择器
  const regions = getRegions();
  console.log('Available regions:', regions);
  const regionOptions = regions.map((region) => ({
    value: region.id,
    text: region.name,
  }));
  initCustomSelect(regionSelect, regionOptions);

  // 初始化国家选择器
  initCustomSelect(countrySelect, []);

  // 地区选择变化事件
  regionSelect.addEventListener('customSelectChange', function (event) {
    const regionValue = /** @type {CustomEvent} */ (event).detail.value;
    console.log('Region changed to:', regionValue);
    updateCountrySelect(regionValue, countrySelect);
    updateRetailers(
      sectionId,
      regionValue,
      getCustomSelectValue(countrySelect),
      tabsContainer,
    );
  });

  // 国家选择变化事件
  countrySelect.addEventListener('customSelectChange', function (event) {
    const countryValue = /** @type {CustomEvent} */ (event).detail.value;
    console.log('Country changed to:', countryValue);
    updateRetailers(
      sectionId,
      getCustomSelectValue(regionSelect),
      countryValue,
      tabsContainer,
    );
  });

  // 选项卡点击事件
  const tabButtons = tabsContainer.querySelectorAll('.tab-button');
  tabButtons.forEach((button) => {
    button.addEventListener('click', function () {
      // 激活当前选项卡
      tabButtons.forEach((btn) => btn.classList.remove('active'));
      this.classList.add('active');

      // 显示对应内容
      const tabContents = tabsContainer.querySelectorAll('.tab-content');
      tabContents.forEach((content) => content.classList.remove('active'));
      const targetTab = tabsContainer.querySelector(
        `#${this.getAttribute('data-tab')}`,
      );
      if (targetTab) targetTab.classList.add('active');

      // 更新零售商显示
      updateRetailers(
        sectionId,
        getCustomSelectValue(regionSelect),
        getCustomSelectValue(countrySelect),
        tabsContainer,
      );
    });
  });

  // 初始化显示
  console.log('Initializing display...');
  updateCountrySelect('all', countrySelect);
  updateRetailers(sectionId, 'all', 'all', tabsContainer);
  console.log('Display initialized');
}

/**
 * 更新国家选择器
 * @param {string} regionId - 地区ID
 * @param {HTMLElement} countrySelect - 国家选择器元素
 */
function updateCountrySelect(regionId, countrySelect) {
  console.log('updateCountrySelect called with regionId:', regionId);

  /** @type {{value: string, text: string}[]} */
  let countryOptions = [];

  if (regionId === 'all') {
    // 显示所有国家
    const allCountries = getAllCountries();
    countryOptions = allCountries.map((country) => ({
      value: country.id,
      text: country.name,
    }));
  } else {
    // 显示特定地区的国家
    const regionCountries = getCountriesByRegion(regionId);
    countryOptions = regionCountries.map((country) => ({
      value: country.id,
      text: country.name,
    }));
  }

  console.log('Country options:', countryOptions);

  // 更新自定义选择器选项
  updateCustomSelectOptions(countrySelect, countryOptions);
  // 重置为默认选择
  setCustomSelectValue(countrySelect, 'all');
}

/**
 * 更新零售商显示 - 按国家分组
 * @param {string} sectionId - section ID
 * @param {string} regionId - 地区ID
 * @param {string} countryId - 国家ID
 * @param {HTMLElement} tabsContainer - 选项卡容器元素
 */
function updateRetailers(sectionId, regionId, countryId, tabsContainer) {
  console.log('updateRetailers called with:', {
    sectionId,
    regionId,
    countryId,
  });

  const activeTab = /** @type {HTMLElement | null} */ (
    tabsContainer.querySelector('.tab-button.active')
  );
  if (!activeTab) {
    console.error('No active tab found');
    return;
  }

  const dataTab = activeTab.getAttribute('data-tab');
  if (!dataTab) {
    console.error('No data-tab attribute found on active tab');
    return;
  }

  const retailerType = dataTab.replace('tab-', '');
  console.log('Retailer type:', retailerType);
  const retailersContainer = /** @type {HTMLElement | null} */ (
    tabsContainer.querySelector(`#${dataTab} .tab-content-inner`)
  );
  if (!retailersContainer) {
    console.error('Retailers container not found for:', dataTab);
    return;
  }

  // 清空现有内容
  retailersContainer.textContent = '';

  // 获取要显示的国家列表
  /** @type {Array<{countryId: string, countryName: string, regionId: string}>} */
  let countriesToShow = [];

  if (countryId === 'all') {
    // 显示所有国家或特定地区的国家
    if (regionId === 'all') {
      // 所有国家
      const allCountries = getAllCountries();
      countriesToShow = allCountries.map((country) => ({
        countryId: country.id,
        countryName: country.name,
        regionId: country.regionId,
      }));
    } else {
      // 特定地区的国家
      const regionCountries = getCountriesByRegion(regionId);
      countriesToShow = regionCountries.map((country) => ({
        countryId: country.id,
        countryName: country.name,
        regionId: regionId,
      }));
    }
  } else {
    // 特定国家
    const allCountries = getAllCountries();
    const targetCountry = allCountries.find((c) => c.id === countryId);
    if (targetCountry) {
      countriesToShow = [
        {
          countryId: targetCountry.id,
          countryName: targetCountry.name,
          regionId: targetCountry.regionId,
        },
      ];
    }
  }

  // 过滤出有对应类型零售商的国家
  countriesToShow = countriesToShow.filter(({ countryId, regionId }) => {
    const retailers = getRetailersByCountry(regionId, countryId, retailerType);
    return retailers.length > 0;
  });

  // 按国家名称A-Z排序
  countriesToShow.sort((a, b) =>
    a.countryName.localeCompare(b.countryName, undefined, {
      sensitivity: 'base',
    }),
  );

  console.log('Countries to show:', countriesToShow);

  // 如果没有符合条件的国家，显示提示
  if (countriesToShow.length === 0) {
    const noRetailersMessage = document.createElement('p');
    noRetailersMessage.className = 'no-retailers';
    noRetailersMessage.textContent = 'No results found for the selected criteria.';
    retailersContainer.appendChild(noRetailersMessage);
    return;
  }

  // 创建国家容器
  const countriesWrapper = document.createElement('div');
  countriesWrapper.className = 'countries-wrapper';

  // 初始显示数量
  const initialShowCount = 3;
  let currentShowCount = initialShowCount;

  // 渲染国家列表的函数
  const renderCountries = (/** @type {number} */ count, /** @type {string} */ activeTabName) => {
    countriesWrapper.textContent = '';
    const countriesToRender = countriesToShow.slice(0, count);

    const toSafeUrl = (/** @type {unknown} */ value) => {
      if (typeof value !== 'string' || value.trim() === '') {
        return '';
      }

      try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch {
        return '';
      }
    };

    countriesToRender.forEach(({ countryId, countryName, regionId }) => {
      const countryGroup = document.createElement('div');
      countryGroup.className = 'country-group';

      const countryTitle = document.createElement('h3');
      countryTitle.className = 'country-title';
      countryTitle.textContent = countryName;
      countryGroup.appendChild(countryTitle);

      const retailersGrid = document.createElement('div');
      retailersGrid.className = 'retailers-grid';

      // 获取该国家的零售商
      const retailers = getRetailersByCountry(
        regionId,
        countryId,
        retailerType,
      );

      // 添加该国家的零售商
      retailers.forEach(
        /** @param {RetailerInfo} retailer */ (retailer, index) => {
          const retailerElement = document.createElement('div');
          retailerElement.className = 'retailer-item';
          const channelUrl = toSafeUrl(retailer.channel_url);
          retailerElement.setAttribute('data-track-event', 'click_button');
          retailerElement.setAttribute('data-track-event-parameters', JSON.stringify({
            module: activeTabName,
            button_name: `${countryName}:${retailer.type || ''}`,
            href: channelUrl,
            position: index + 1,
          }));
          const logoUrl = toSafeUrl(retailer.logo_url);
          const logoContainer = document.createElement('div');
          logoContainer.className = 'retailer-logo';
          if (logoUrl) {
            const image = document.createElement('img');
            image.setAttribute('src', logoUrl);
            image.alt = retailer.type || '';
            image.loading = 'lazy';
            logoContainer.appendChild(image);
          } else {
            const nameElement = document.createElement('div');
            nameElement.className = 'retailer-name';
            nameElement.textContent = retailer.type || '';
            logoContainer.appendChild(nameElement);
          }
          retailerElement.appendChild(logoContainer);

          // 添加点击事件跳转到零售商网站
          if (channelUrl) {
            retailerElement.style.cursor = 'pointer';
            retailerElement.addEventListener('click', function () {
              window.open(channelUrl, '_blank');
            });
          }

          retailersGrid.appendChild(retailerElement);
        },
      );

      countryGroup.appendChild(retailersGrid);
      countriesWrapper.appendChild(countryGroup);
    });
  };
  const activeTabName = (activeTab?.textContent ?? '').trim();
  // 初始渲染
  renderCountries(currentShowCount, activeTabName);
  retailersContainer.appendChild(countriesWrapper);

  // 如果国家数量大于初始显示数量，添加 View More 按钮
  if (countriesToShow.length > initialShowCount) {
    const viewMoreWrapper = document.createElement('div');
    viewMoreWrapper.className = 'view-more-wrapper';

    const viewMoreButton = document.createElement('button');
    viewMoreButton.className = 'view-more-button ug-spx-button-secondary';
    viewMoreButton.textContent = UGGlobalText?.view_more || 'View More';

    viewMoreButton.addEventListener('click', function () {
      currentShowCount += 3;
      renderCountries(currentShowCount, activeTabName);

      // 如果已经显示所有国家，隐藏按钮
      if (currentShowCount >= countriesToShow.length) {
        viewMoreWrapper.style.display = 'none';
      }
    });

    viewMoreWrapper.appendChild(viewMoreButton);
    retailersContainer.appendChild(viewMoreWrapper);
  }
}

/**
 * 初始化自定义选择器
 * @param {HTMLElement} selectElement - 选择器元素
 * @param {{value: string, text: string}[]} options - 选项数组
 */
function initCustomSelect(selectElement, options) {
  const trigger = selectElement.querySelector('.select-trigger');
  const dropdown = selectElement.querySelector('.select-dropdown');

  if (!trigger || !dropdown) return;

  // 添加选项
  updateCustomSelectOptions(selectElement, options);

  // 点击触发器切换下拉菜单
  trigger.addEventListener('click', function (e) {
    e.stopPropagation();
    closeAllCustomSelects();
    selectElement.classList.toggle('active');
    trigger.classList.toggle('active');
  });

  // 点击选项
  dropdown.addEventListener('click', function (e) {
    const option = /** @type {HTMLElement} */ (e.target).closest(
      '.select-option',
    );
    if (option) {
      const value = option.getAttribute('data-value');
      const text = option.textContent;

      if (value && text) {
        setCustomSelectValue(selectElement, value, text);

        // 触发自定义事件
        const changeEvent = new CustomEvent('customSelectChange', {
          detail: { value, text },
        });
        selectElement.dispatchEvent(changeEvent);
      }

      selectElement.classList.remove('active');
      trigger.classList.remove('active');
    }
  });

  // 键盘支持
  trigger.setAttribute('tabindex', '0');
  trigger.addEventListener('keydown', function (e) {
    const keyEvent = /** @type {KeyboardEvent} */ (e);
    if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
      keyEvent.preventDefault();
      /** @type {HTMLElement} */ (trigger).click();
    } else if (keyEvent.key === 'Escape') {
      selectElement.classList.remove('active');
      trigger.classList.remove('active');
    }
  });
}

/**
 * 更新自定义选择器选项
 * @param {HTMLElement} selectElement - 选择器元素
 * @param {{value: string, text: string}[]} options - 选项数组
 */
function updateCustomSelectOptions(selectElement, options) {
  const dropdown = selectElement.querySelector('.select-dropdown');
  if (!dropdown) return;

  // 保留默认的 "All" 选项
  const defaultOption = dropdown.querySelector(
    '.select-option[data-value="all"]',
  );
  dropdown.textContent = '';

  if (defaultOption) {
    dropdown.appendChild(defaultOption);
  }

  // 添加新选项
  options.forEach((option) => {
    const optionElement = document.createElement('div');
    optionElement.className = 'select-option';
    optionElement.setAttribute('data-value', option.value);
    optionElement.textContent = option.text;
    dropdown.appendChild(optionElement);
  });
}

/**
 * 设置自定义选择器的值
 * @param {HTMLElement} selectElement - 选择器元素
 * @param {string} value - 值
 * @param {string} [text] - 显示文本（可选）
 */
function setCustomSelectValue(selectElement, value, text) {
  const textElement = selectElement.querySelector('.select-text');
  const dropdown = selectElement.querySelector('.select-dropdown');

  if (!textElement || !dropdown) return;

  // 更新显示文本
  if (text) {
    textElement.textContent = text;
  } else {
    const option = dropdown.querySelector(`[data-value="${value}"]`);
    if (option) {
      textElement.textContent = option.textContent || '';
    }
  }

  // 更新选中状态
  const allOptions = dropdown.querySelectorAll('.select-option');
  allOptions.forEach((option) => {
    option.classList.remove('selected');
    if (option.getAttribute('data-value') === value) {
      option.classList.add('selected');
    }
  });

  // 存储当前值
  selectElement.setAttribute('data-value', value);
}

/**
 * 获取自定义选择器的值
 * @param {HTMLElement} selectElement - 选择器元素
 * @returns {string} 当前值
 */
function getCustomSelectValue(selectElement) {
  return selectElement.getAttribute('data-value') || 'all';
}

/**
 * 关闭所有自定义选择器
 */
function closeAllCustomSelects() {
  const allSelects = document.querySelectorAll('.custom-select');
  allSelects.forEach((select) => {
    select.classList.remove('active');
    const trigger = select.querySelector('.select-trigger');
    if (trigger) {
      trigger.classList.remove('active');
    }
  });
}

// 点击外部关闭选择器
document.addEventListener('click', function () {
  closeAllCustomSelects();
});

/**
 * 初始化 sticky tabs header 功能
 * @param {string} sectionId - section ID
 */
function initStickyTabsHeader(sectionId) {
  const tabsHeader = /** @type {HTMLElement | null} */ (
    document.querySelector(
      `.where-to-buy-tabs[data-section-id="${sectionId}"] .ug-wtb-tabs-header`,
    )
  );

  if (!tabsHeader) {
    console.warn('Tabs header not found for section:', sectionId);
    return;
  }

  // 滚动方向检测
  let lastScrollY = window.scrollY;
  let scrollDirection = 'down';

  // 计算并设置 header 高度
  const updateTabsHeaderTop = () => {
    const headerHeight = getHeaderHeight();
    const currentScrollY = window.scrollY;
    const isMobile = window.innerWidth <= 768;

    // 检测滚动方向
    if (currentScrollY > lastScrollY) {
      scrollDirection = 'down';
    } else if (currentScrollY < lastScrollY) {
      scrollDirection = 'up';
    }
    lastScrollY = currentScrollY;

    // 移动端和桌面端都考虑 header 高度
    // 当向上滚动时（header 显示），tabs header 应该在 header 下方
    // 当向下滚动时（header 隐藏），tabs header 可以贴顶
    if (isMobile) {
      if (scrollDirection === 'up' && currentScrollY > 0) {
        // 向上滚动且不在顶部时，考虑 header 高度
        tabsHeader.style.top = `${headerHeight}px`;
      } else {
        // 向下滚动或在顶部时，贴顶显示
        tabsHeader.style.top = '0px';
      }
    } else {
      // 桌面端始终考虑 header 高度
      // 使用 header 高度减1像素确保完全遮盖
      tabsHeader.style.top = `${Math.max(0, headerHeight - 1)}px`;
    }
  };

  // 初始设置 - 延迟执行确保页面完全加载
  setTimeout(() => {
    updateTabsHeaderTop();
  }, 100);

  // 立即设置一个临时值避免闪烁
  const initialHeaderHeight = getHeaderHeight();
  const isMobile = window.innerWidth <= 768;
  if (isMobile) {
    tabsHeader.style.top = '0px';
  } else {
    tabsHeader.style.top = `${Math.max(0, initialHeaderHeight - 1)}px`;
  }

  // 监听窗口大小变化和滚动
  /** @type {number | null} */
  let resizeTimeout = null;
  /** @type {number | null} */
  let scrollTimeout = null;

  const handleResize = () => {
    if (resizeTimeout !== null) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateTabsHeaderTop, 100);
  };

  const handleScroll = () => {
    if (scrollTimeout !== null) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(updateTabsHeaderTop, 50);
  };

  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll, { passive: true });

  // 监听 header 高度变化（如果有相关事件）
  const observer = new MutationObserver(() => {
    updateTabsHeaderTop();
  });

  // 观察 body 的 style 属性变化（header 高度可能通过 CSS 变量更新）
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['style'],
  });

  // 清理函数
  const cleanup = () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('scroll', handleScroll);
    observer.disconnect();
    if (resizeTimeout !== null) clearTimeout(resizeTimeout);
    if (scrollTimeout !== null) clearTimeout(scrollTimeout);
  };

  // 存储清理函数以便后续使用
  /** @type {any} */ (tabsHeader)._stickyCleanup = cleanup;
}

/**
 * 获取页面 header 的高度
 * @returns {number} header 高度（像素）
 */
function getHeaderHeight() {
  // 尝试从 CSS 变量获取 header 高度
  const headerHeightVar = getComputedStyle(document.body).getPropertyValue(
    '--header-height',
  );
  if (headerHeightVar && headerHeightVar !== '0px') {
    const height = parseInt(headerHeightVar, 10);
    if (height > 0) return height;
  }

  // 如果 CSS 变量不可用，直接计算 header 元素高度
  const header = /** @type {HTMLElement | null} */ (
    document.querySelector('#header-component')
  );
  if (header) {
    const rect = header.getBoundingClientRect();
    const computedStyle = getComputedStyle(header);

    // 考虑边框和边距
    const borderTop = parseInt(computedStyle.borderTopWidth, 10) || 0;
    const borderBottom = parseInt(computedStyle.borderBottomWidth, 10) || 0;
    const marginTop = parseInt(computedStyle.marginTop, 10) || 0;
    const marginBottom = parseInt(computedStyle.marginBottom, 10) || 0;

    return rect.height + borderTop + borderBottom + marginTop + marginBottom;
  }

  // 尝试获取 header-group 的高度
  const headerGroup = /** @type {HTMLElement | null} */ (
    document.querySelector('#header-group')
  );
  if (headerGroup) {
    return headerGroup.offsetHeight;
  }

  // 默认返回 0
  return 0;
}

// 导出函数供全局使用
window.loadWhereToBuyData = loadWhereToBuyData;
window.getWhereToBuyData = getWhereToBuyData;
window.initWhereToBuySection = initWhereToBuySection;

// 添加全局调试函数
/** @type {any} */ (window).debugWhereToBuy = function () {
  console.log('=== Where to Buy Debug Info ===');
  console.log('whereToBuyData:', whereToBuyData);
  console.log('Available functions:', {
    loadWhereToBuyData: typeof window.loadWhereToBuyData,
    getWhereToBuyData: typeof window.getWhereToBuyData,
    initWhereToBuySection: typeof window.initWhereToBuySection,
  });

  // 测试数据加载
  loadWhereToBuyData()
    .then((data) => {
      console.log('Data loaded successfully:', data);
      console.log('Regions:', Object.keys(data || {}));
      console.log(
        'Sample region data:',
        data ? Object.keys(data)[0] : 'No data',
      );
    })
    .catch((error) => {
      console.error('Data loading failed:', error);
    });
};
